import React from 'react';
import isNil from 'lodash/isNil';
import { NumberInput, useResourceContext } from 'react-admin';
import { round } from 'utils/number';
import { Alert } from '@medely/ui-kit/web';
import { Alert as MuiAlert } from '@mui/material';
import { BaseChargeRateCentsInput } from './rate-inputs/BaseChargeRateCentsInput';
import { ChargeRateCentsInput } from './rate-inputs/ChargeRateCentsInput';
import { RateStructureSelect } from './rate-inputs/RateStructureSelect';
import { BlendedRateCentsInput } from './rate-inputs/BlendedRateCentsInput';
import { BlendedRateMarkupInput } from './rate-inputs/BlendedRateMarkupInput';
import { MarginPercentInput } from './rate-inputs/MarginPercentInput';
import { BaseMarginPercentInput } from './rate-inputs/BaseMarginPercentInput';
import { RateSettingsDisplayHelper } from 'utils/displayHelpers/rate-settings';
import { getInheritedValue } from 'utils/rateSettings/SharedRateSettingsForm.utils';
import { getClassificationFromSource } from './PositionRateSettingsForm.utils';
import {
  calculatedFieldPath,
  useRecordValueReader,
} from 'resources/rate_settings/utils/fieldsVisibility';
import { ProfessionalRateInput } from './rate-inputs/ProfessionalRateInput';
import { BaseMarginPercentW2LocalInput } from './rate-inputs/BaseMarginPercentW2LocalInput';
import { RateControllerProvider } from './rateController/RateControllerContext';
import { useRateController } from './rateController/useRateController';

type Props = {
  source: string;
  inheritedSettings: any;
  taxClassification: string;
};

const LocationAndProfessionalRateInput = ({
  source,
  inheritedSettings = {},
  taxClassification,
}: Props) => {
  const resource = useResourceContext();
  const recordValue = useRecordValueReader();
  const controller = useRateController({ source, inheritedSettings, resource });
  const { warnings } = controller;

  const isTen99 = source.includes('ten99');
  const isW2Local = source.includes('w2.local');
  const isW2Travel = source.includes('w2.travel');
  const { tab } = getClassificationFromSource(source);

  const rateSettingsDisplayHelper = new RateSettingsDisplayHelper({ settings: inheritedSettings });
  const inheritedSettingsHash = rateSettingsDisplayHelper.getSettingsForClassification(tab) ?? {};

  const inheritedW2TcvMargin = getInheritedValue(inheritedSettingsHash, 'margin_percent_w2_tcv');
  const w2TcvMarginHelperText = isNil(inheritedW2TcvMargin)
    ? ''
    : String(round(Number(inheritedW2TcvMargin), 5));

  const isLocationLevelPositionRateSetting =
    resource === 'LocationPositionRateSetting' || resource === 'AgencyLocationPositionRateSetting';
  const show12hRatesFields = !isLocationLevelPositionRateSetting;
  const showRateStructureSelect =
    (['MarketPositionRateSetting'].includes(resource) || isLocationLevelPositionRateSetting) &&
    !isW2Travel;

  return (
    <RateControllerProvider value={controller}>
      {warnings.length > 0 && <MuiAlert severity="warning">{warnings.join(' ')}</MuiAlert>}

      {showRateStructureSelect && (
        <RateStructureSelect source={source} inheritedSettings={inheritedSettingsHash} />
      )}

      <ProfessionalRateInput source={source} inheritedSettings={inheritedSettingsHash} />

      <BaseChargeRateCentsInput source={source} inheritedSettings={inheritedSettingsHash} />

      <ChargeRateCentsInput source={source} inheritedSettings={inheritedSettingsHash} />

      {show12hRatesFields && (
        <>
          <BlendedRateCentsInput source={source} inheritedSettings={inheritedSettingsHash} />
          <Alert
            title="12 hour rate structure"
            description="Please set the max hour fields appropriately based on the OT/XR policy that needs to be applied to the location and position."
            variant="filled"
            hideCloseIcon
          />

          <BlendedRateMarkupInput source={source} inheritedSettings={inheritedSettingsHash} />
        </>
      )}

      {isTen99 && (
        <BaseMarginPercentInput
          source={source}
          field="base_margin_percent"
          label="Base Margin Percent"
          inheritedSettings={inheritedSettingsHash}
          controllable
        />
      )}

      {isW2Local && (
        <BaseMarginPercentW2LocalInput source={source} inheritedSettings={inheritedSettingsHash} />
      )}

      {isW2Travel && (
        <>
          <BaseMarginPercentInput
            source={source}
            field="base_margin_percent_w2_tcv"
            label="Base Margin Percent W2 TCV"
            inheritedSettings={inheritedSettingsHash}
          />
          <BaseMarginPercentInput
            source={source}
            field="base_margin_percent_w2_travel"
            label="Base Margin Percent W2 Travel"
            inheritedSettings={inheritedSettingsHash}
            controllable
          />
          <NumberInput
            source={calculatedFieldPath(source, 'margin_percent_w2_tcv', recordValue)}
            label="Margin Percent W2 TCV"
            disabled
            helperText={w2TcvMarginHelperText}
          />
        </>
      )}

      <MarginPercentInput source={source} inheritedSettings={inheritedSettings} />
    </RateControllerProvider>
  );
};

export default LocationAndProfessionalRateInput;
